import { useState, useEffect } from 'react';
import { colors } from '../constants/colors';
import { AlertTriangle, Check } from 'lucide-react';
import { getCarrierPackages } from '../api/client';
import type { CarrierPackage } from '../types';

const CARRIER_LOGOS: Record<string, string> = {
  USPS: '/img/logos/carrier-usps.svg',
  FedEx: '/img/logos/carrier-fx.svg',
};

type PackagingMode = 'carrier' | 'custom';

interface PackagingState {
  mode: PackagingMode;
  weightLbs: string;
  weightOz: string;
  lengthIn: string;
  widthIn: string;
  heightIn: string;
  carrierPackageId: number | null;
}

interface PackagingSelectorProps {
  state: PackagingState;
  onChange: (state: PackagingState) => void;
}

export type { PackagingState };

export function usePackagingState(initialWeightOz?: string, initialLengthIn?: string, initialWidthIn?: string, initialHeightIn?: string): [PackagingState, (s: PackagingState) => void] {
  const [state, setState] = useState<PackagingState>(() => {
    let weightLbs = '';
    let weightOz = '';
    if (initialWeightOz) {
      const totalOz = parseFloat(initialWeightOz);
      if (totalOz > 0) {
        const lbs = Math.floor(totalOz / 16);
        const oz = Math.round((totalOz % 16) * 100) / 100;
        weightLbs = lbs > 0 ? String(lbs) : '';
        weightOz = oz > 0 ? String(oz) : '';
      }
    }
    return {
      mode: 'carrier',
      weightLbs,
      weightOz,
      lengthIn: initialLengthIn || '',
      widthIn: initialWidthIn || '',
      heightIn: initialHeightIn || '',
      carrierPackageId: null,
    };
  });
  return [state, setState];
}

export function getTotalWeightOz(state: PackagingState): number {
  const lbs = parseFloat(state.weightLbs) || 0;
  const oz = parseFloat(state.weightOz) || 0;
  return lbs * 16 + oz;
}

export function isPackagingValid(state: PackagingState): boolean {
  const w = getTotalWeightOz(state);
  if (w <= 0) return false;
  if (state.mode === 'carrier') return state.carrierPackageId !== null;
  return !!(parseFloat(state.lengthIn) && parseFloat(state.widthIn) && parseFloat(state.heightIn));
}

export function buildRateData(state: PackagingState): { weightOz: number; lengthIn?: number; widthIn?: number; heightIn?: number; carrierPackageId?: number } {
  const data: { weightOz: number; lengthIn?: number; widthIn?: number; heightIn?: number; carrierPackageId?: number } = {
    weightOz: getTotalWeightOz(state),
  };
  if (state.mode === 'carrier' && state.carrierPackageId) {
    data.carrierPackageId = state.carrierPackageId;
  } else {
    data.lengthIn = parseFloat(state.lengthIn);
    data.widthIn = parseFloat(state.widthIn);
    data.heightIn = parseFloat(state.heightIn);
  }
  return data;
}

export function PackagingSelector({ state, onChange }: PackagingSelectorProps) {
  const [packages, setPackages] = useState<CarrierPackage[]>([]);
  const [selectedCarrier, setSelectedCarrier] = useState<string | null>(null);

  useEffect(() => {
    getCarrierPackages().then(setPackages).catch(() => {});
  }, []);

  const carriers = [...new Set(packages.map(p => p.carrier))];
  const selectedPkg = packages.find(p => p.id === state.carrierPackageId);

  const update = (partial: Partial<PackagingState>) => onChange({ ...state, ...partial });

  return (
    <>
      {/* Mode Toggle */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Packaging</h3>
        <div style={styles.toggle}>
          <button
            style={{ ...styles.toggleButton, ...(state.mode === 'carrier' ? styles.toggleActive : {}) }}
            onClick={() => update({ mode: 'carrier', carrierPackageId: null })}
          >
            Carrier Packaging
          </button>
          <button
            style={{ ...styles.toggleButton, ...(state.mode === 'custom' ? styles.toggleActive : {}) }}
            onClick={() => update({ mode: 'custom', carrierPackageId: null })}
          >
            Custom Packaging
          </button>
        </div>
      </div>

      {state.mode === 'carrier' ? (
        <>
          {/* Carrier Buttons */}
          <div style={styles.section}>
            <label style={styles.dimLabel}>Carrier</label>
            <div style={styles.carrierRow}>
              {carriers.map(c => (
                <button
                  key={c}
                  style={{
                    ...styles.carrierButton,
                    ...(selectedCarrier === c ? styles.carrierButtonSelected : {}),
                  }}
                  onClick={() => { setSelectedCarrier(c); update({ carrierPackageId: null }); }}
                >
                  {CARRIER_LOGOS[c] && <img src={CARRIER_LOGOS[c]} alt={c} style={{ height: 16 }} />}
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Package List */}
          {selectedCarrier && (
            <div style={styles.section}>
              <label style={styles.dimLabel}>Package</label>
              <div style={styles.packageList}>
                {packages.filter(p => p.carrier === selectedCarrier).map(pkg => {
                  const isSelected = state.carrierPackageId === pkg.id;
                  return (
                    <button
                      key={pkg.id}
                      style={{ ...styles.packageCard, ...(isSelected ? styles.packageCardSelected : {}) }}
                      onClick={() => update({ carrierPackageId: pkg.id })}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: colors.ink.black }}>{pkg.name}</div>
                        {pkg.description && (
                          <div style={{ fontSize: 12, color: colors.gray[600], marginTop: 2 }}>{pkg.description}</div>
                        )}
                      </div>
                      {isSelected && <Check size={16} color={colors.brand.blue} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Callout */}
          {selectedPkg && (
            <div style={styles.callout}>
              <AlertTriangle size={16} color="#D97706" style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#92400E', marginBottom: 4 }}>
                  Did you select the right sized package?
                </div>
                <div style={{ fontSize: 12, color: '#92400E' }}>
                  {selectedPkg.name}: {selectedPkg.description}
                </div>
                <div style={{ fontSize: 11, color: '#B45309', marginTop: 4, lineHeight: 1.5 }}>
                  Please make sure you enter the correct package size and dimensions to avoid additional charges from your chosen carrier.
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Custom Dimensions */
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Package Dimensions</h3>
          <div style={styles.dimGrid}>
            <div style={styles.dimField}>
              <label style={styles.dimLabel}>Length (in)</label>
              <input type="number" step="0.1" min="0" value={state.lengthIn} onChange={e => update({ lengthIn: e.target.value })} style={styles.dimInput} placeholder="e.g. 12" />
            </div>
            <div style={styles.dimField}>
              <label style={styles.dimLabel}>Width (in)</label>
              <input type="number" step="0.1" min="0" value={state.widthIn} onChange={e => update({ widthIn: e.target.value })} style={styles.dimInput} placeholder="e.g. 8" />
            </div>
            <div style={styles.dimField}>
              <label style={styles.dimLabel}>Height (in)</label>
              <input type="number" step="0.1" min="0" value={state.heightIn} onChange={e => update({ heightIn: e.target.value })} style={styles.dimInput} placeholder="e.g. 6" />
            </div>
          </div>
        </div>
      )}

      {/* Weight — always shown */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Package Weight</h3>
        <div style={styles.weightGrid}>
          <div style={styles.dimField}>
            <label style={styles.dimLabel}>Pounds (lb)</label>
            <input type="number" step="1" min="0" value={state.weightLbs} onChange={e => update({ weightLbs: e.target.value })} style={styles.dimInput} placeholder="0" />
          </div>
          <div style={styles.dimField}>
            <label style={styles.dimLabel}>Ounces (oz)</label>
            <input type="number" step="0.1" min="0" value={state.weightOz} onChange={e => update({ weightOz: e.target.value })} style={styles.dimInput} placeholder="0" />
          </div>
        </div>
      </div>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: { marginBottom: 16 },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: colors.gray[600],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 10px 0',
  },
  toggle: {
    display: 'flex',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: 8,
    overflow: 'hidden',
  },
  toggleButton: {
    flex: 1,
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    backgroundColor: colors.gray[100],
    color: colors.ink.black,
  },
  toggleActive: {
    backgroundColor: colors.brand.blue,
    color: colors.white,
  },
  carrierRow: { display: 'flex', gap: 8, marginTop: 4 },
  carrierButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 8,
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: colors.gray[200],
    backgroundColor: colors.gray[100],
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    color: colors.ink.black,
  },
  carrierButtonSelected: {
    borderColor: colors.brand.blue,
    backgroundColor: colors.brand.blue10,
    color: colors.brand.blue,
  },
  packageList: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 },
  packageCard: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    borderRadius: 8,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.gray[200],
    backgroundColor: colors.gray[100],
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
  },
  packageCardSelected: {
    borderColor: colors.brand.blue,
    borderWidth: 2,
    backgroundColor: colors.brand.blue10,
  },
  callout: {
    display: 'flex',
    gap: 8,
    padding: 12,
    backgroundColor: '#FEF3C7',
    borderRadius: 8,
    marginBottom: 16,
  },
  dimGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 10,
  },
  weightGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    maxWidth: 280,
  },
  dimField: { display: 'flex', flexDirection: 'column', gap: 4 },
  dimLabel: { fontSize: 12, fontWeight: 600, color: colors.gray[600] },
  dimInput: {
    width: '100%',
    padding: '8px 10px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: 8,
    fontSize: 14,
    color: colors.ink.black,
    boxSizing: 'border-box',
  },
};
